import { Action } from "~/modules/actions/Action";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { getLogger, LoggerName } from "~/modules/logger";
import { CbmImpl } from "~/modules/cbm";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import {
    checkChannelSid,
    checkInteractionSid,
    checkReservationExists,
    getReservationsByTask
} from "~/modules/actions/ActionUtils";
import { sendWorkerTrackingEvent, toCauseMessage } from "~/modules/telemetry/utils";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { createFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";





























export class PauseConversation implements Action<Promise<void>> {
    


    readonly #taskSid: string;

    constructor(taskSid: string) {
        this.#taskSid = taskSid;
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const logger = getLogger(context)(LoggerName.Actions);

        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const worker = taskRouter.worker;

        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();
        logger.debug(`PauseConversation invoked with taskSid: ${this.#taskSid}`);

        const reservation = getReservationsByTask(worker, this.#taskSid)[0];

        checkReservationExists(reservation, this.#taskSid, {
            actionName: "PauseConversation",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.PauseConversationCompleted,
            extra: { taskSid: this.#taskSid },
            logger
        });

        const task = reservation.task;

        const interactionSid = task.attributes.flexInteractionSid as string | undefined;
        const channelSid = task.attributes.flexInteractionChannelSid as string | undefined;

        checkInteractionSid(interactionSid, this.#taskSid, {
            actionName: "PauseConversation",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.PauseConversationCompleted,
            extra: { taskSid: this.#taskSid },
            logger
        });

        checkChannelSid(channelSid, this.#taskSid, {
            actionName: "PauseConversation",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.PauseConversationCompleted,
            extra: { taskSid: this.#taskSid },
            logger
        });

        try {
            await cbm.pauseChannel(interactionSid!, channelSid!, "pause", "closed");

            sendWorkerTrackingEvent(EVENTS.PauseConversationCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                interactionSid: interactionSid!,
                channelSid: channelSid!
            });

            return Promise.resolve();
        } catch (error) {
            const errorMsg = `PauseConversation: Failed to pause conversation for interaction ${channelSid} and task ${
                this.#taskSid
            }`;
            logger.error(errorMsg, error);

            sendWorkerTrackingEvent(EVENTS.PauseConversationCompleted, analytics, worker, {
                error: `${errorMsg} (${toCauseMessage(error)})`,
                taskSid: this.#taskSid,
                interactionSid: interactionSid!,
                channelSid: channelSid!
            });

            return Promise.reject(
                createFlexSdkError(context)(
                    ErrorCode.FailedToPauseConversation,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                )
            );
        }
    }
}
